/**
 * Create Project — name + description only.
 *
 * Client, team members and links are NOT asked at creation time. They
 * are added later from the Project page (Team tab) and the bot's
 * `/projects ⚙️` menu, so first-time creation stays a two-field form.
 * See ADR 0009 (amended 2026-06-06).
 *
 * The native Telegram MainButton (bottom-screen primary CTA) replaces
 * the in-page "Create project" button. BackButton is wired by the
 * `useBackButton` hook from PR 1. No in-page Cancel control either —
 * the BackButton is the canonical "leave this page" gesture.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { createProject } from "../api/projects";
import { useBackButton } from "../hooks/useBackButton";
import { useMainButton } from "../hooks/useMainButton";
import type { CreateProjectRequest } from "../lib/types";

export function CreateProject(): React.ReactElement {
  const [params] = useSearchParams();
  const workspaceId = params.get("workspace") ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useBackButton(useCallback(() => navigate("/"), [navigate]));

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: CreateProjectRequest) => createProject(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
      navigate("/");
    },
  });

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  const submit = useCallback((): void => {
    setValidationError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError("Project name is required.");
      return;
    }
    // Client / members / links are filled in later from the Project page
    // (Team tab) — creation only needs name + description. We still send
    // empty arrays because the request schema requires the keys.
    const payload: CreateProjectRequest = {
      workspace_id: workspaceId,
      name: trimmedName,
      ...(description.trim() ? { description: description.trim() } : {}),
      members: [],
      links: [],
    };
    mutation.mutate(payload);
  }, [description, mutation, name, workspaceId]);

  useMainButton(
    useMemo(
      () => ({
        text: mutation.isPending ? "Creating…" : "Create project",
        onClick: submit,
        loading: mutation.isPending,
        disabled: !canSubmit,
        // Hide when the workspace param is missing (we render an error
        // state below instead — the MainButton would be confusing).
        visible: Boolean(workspaceId),
      }),
      [canSubmit, mutation.isPending, submit, workspaceId],
    ),
  );

  if (!workspaceId) {
    return (
      <CenteredError
        title="Missing workspace"
        message="This page needs a ?workspace=<uuid> query parameter."
        onPrimary={() => navigate("/")}
        primaryLabel="Back"
      />
    );
  }

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error instanceof Error
        ? mutation.error.message
        : null;

  return (
    <div
      className="min-h-dvh"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <Header />

      <form
        // Pad the bottom so the MainButton (native, ~56 px) never
        // overlaps the last field.
        className="px-4 py-3 space-y-4 pb-24"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <Field label="Name" required error={validationError ?? undefined}>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g. Acme Q3 Launch"
            maxLength={200}
            required
          />
        </Field>

        <Field label="Description">
          <TextArea
            value={description}
            onChange={setDescription}
            rows={3}
            maxLength={2000}
            placeholder="What's this project about?"
          />
        </Field>

        <p className="text-xs" style={{ color: "var(--color-hint)" }}>
          You can add the client, team members and links later from the
          project page.
        </p>

        {serverError ? (
          <p
            className="text-sm"
            style={{ color: "var(--color-destructive)" }}
            role="alert"
          >
            {serverError}
          </p>
        ) : null}

        {/* Hidden submit button: lets `Enter` in inputs submit the
            form without us needing to wire keyboard shortcuts. The
            primary CTA is the native MainButton. */}
        <button type="submit" hidden disabled={!canSubmit} aria-hidden="true" />
      </form>
    </div>
  );
}

/* ---------- Layout primitives ----------------------------------------- */

function Header(): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-10 px-4 py-3"
      style={{
        background: "var(--color-bg)",
        borderBottom: "1px solid var(--color-secondary-bg)",
      }}
    >
      <h1 className="text-lg font-semibold">New project</h1>
    </header>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label className="block">
      <div
        className="text-[11px] uppercase tracking-wide mb-1"
        style={{ color: "var(--color-hint)" }}
      >
        {label}
        {required ? " *" : ""}
      </div>
      {children}
      {error ? (
        <p
          className="text-xs mt-1"
          style={{ color: "var(--color-destructive)" }}
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </label>
  );
}

const INPUT_CLASSES = "w-full rounded-lg px-3 py-2 text-sm outline-none";
function inputStyle(): React.CSSProperties {
  return {
    background: "var(--color-secondary-bg)",
    color: "var(--color-text)",
    border: "1px solid transparent",
  };
}

function TextInput(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}): React.ReactElement {
  return (
    <input
      type="text"
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      maxLength={props.maxLength}
      required={props.required}
      className={INPUT_CLASSES}
      style={inputStyle()}
    />
  );
}

function TextArea(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}): React.ReactElement {
  return (
    <textarea
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      maxLength={props.maxLength}
      rows={props.rows}
      className={`${INPUT_CLASSES} resize-y`}
      style={inputStyle()}
    />
  );
}

function CenteredError({
  title,
  message,
  onPrimary,
  primaryLabel,
}: {
  title: string;
  message?: string;
  onPrimary: () => void;
  primaryLabel: string;
}): React.ReactElement {
  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center gap-3 px-6 text-center"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <h2 className="text-lg font-semibold">{title}</h2>
      {message ? (
        <p className="text-sm" style={{ color: "var(--color-hint)" }}>
          {message}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onPrimary}
        className="rounded-full px-4 py-2 text-sm"
        style={{
          background: "var(--color-button)",
          color: "var(--color-button-text)",
        }}
      >
        {primaryLabel}
      </button>
    </div>
  );
}
